/* eslint-disable guard-for-in */
import moment from "moment";
// Import from the leaf module, NOT commonUtility: commonUtility transitively
// imports every FusionCharts widget (via componentsTransfomation), and this
// file is used by initial-load screens (TopNav, org listing, login).
import { create2DArray } from "../../containers/DashboardDesigner/tableTransformUtils";
// DrillByService only pulls in axios/lodash-weight API helpers (verified: no transitive
// import of commonUtility/componentsTransfomation), so reusing its sort logic here doesn't
// risk the "every FusionCharts widget" import blowup called out above.
import { applySortOrder } from "../../components/DashboardDesigner/DrillByService";
/**
 * List of widget services
 */

/**
 * Receives the redux store object of a widget
 * and returns whether its a filter or not
 */
export const isFilter = (widget, widgetHashConfig) => {
	return (
		widget &&
		widgetHashConfig[widget.refWidgetId] &&
		widgetHashConfig[widget.refWidgetId].refComponentId === "FILTERS"
	);
};

/**
 * Receives the redux store object of a widget
 * and returns whether its a Date filter or not
 */
export const isDateFilter = (widget, widgetHashConfig) => {
	return (
		widget &&
		widgetHashConfig[widget.refWidgetId] &&
		widgetHashConfig[widget.refWidgetId].refComponentId === "FILTERS" &&
		widgetHashConfig[widget.refWidgetId].type === "DATE_FILTER"
	);
};

/**
 * Receives the redux store object of a widget
 * and returns whether its a component (not filter)
 * whose placement is `components`
 */
export const isBodyComponent = (widget, widgetHashConfig) => {
	return (
		widget &&
		widget.placement === "components" &&
		widgetHashConfig[widget.refWidgetId] &&
		widgetHashConfig[widget.refWidgetId].refComponentId !== "FILTERS"
	);
};

/**
 * Receives the redux store object of a widget
 * and returns whether its a filter
 * whose placement is `components`
 */
export const isLocalFilter = (widget, widgetHashConfig) => {
	return (
		widget &&
		widget.placement === "components" &&
		widgetHashConfig[widget.refWidgetId] &&
		widgetHashConfig[widget.refWidgetId].refComponentId === "FILTERS"
	);
};

export const getNumberFromFormattedChar = char => {
	if (typeof char === "number") {
		return char;
	}
	let str = typeof char === "string" ? char : "";
	// If there is a comma after . then it's UK formatted
	const firstCommaIndex = str.indexOf(",");
	const firstDotIndex = str.indexOf(".");
	if (firstCommaIndex > -1 && firstDotIndex > -1 && firstCommaIndex > firstDotIndex) {
		/*
			1. Replace all . by ,
			2. Replace the last , of the resulting str by .
		*/
		str = str.replace(/[.]/g, ",");
		const index = str.lastIndexOf(",");
		str = `${str.substring(0, index)}.${str.substring(index + 1)}`;
	}
	return str.includes("- ") ? str.replace(/[^0-9.]/g, "") : parseFloat(str.replace(/[^0-9.-]/g, ""));
};

export const customSortMethod = (a, b) => {
	const val1 = getNumberFromFormattedChar(a);
	const val2 = getNumberFromFormattedChar(b);
	if (val1 === val2) {
		return 0;
	}
	return val1 > val2 ? 1 : -1;
};

/**
 * Specifically for Presets Filter
 * Returns: presets option for filter or dynamic start and end date using moment based on the range selected.
 * @param wantObj {return obj depending on whether start and end date is needed or presets obj is needed}
 */
export const getStartEndDateForPresetsFilter = (selectedRange, startMonth, minDate, maxDate, wantObj) => {
	const obj = {
		Fixed: {
			label: "Fixed",
			start: moment
				.utc()
				.subtract(1, "weeks")
				.startOf("week"),
			end: moment
				.utc()
				.subtract(1, "weeks")
				.endOf("week")
		},
		Today: {
			label: "Today",
			start: moment.utc(),
			end: moment.utc()
		},
		"Last 30 days": {
			label: "Last 30 days",
			start: moment.utc().subtract(29, "days"),
			end: moment.utc()
		},
		"This Month to date": {
			label: "This Month to date",
			start: moment.utc().startOf("month"),
			end: moment.utc()
		},
		"This Year to date": {
			label: "This Year to date",
			start: moment.utc().startOf("year"),
			end: moment.utc()
		},
		"This Financial Year to date": {
			label: "This Financial Year to date",
			start:
				moment.utc().month() < startMonth - 1
					? moment
							.utc()
							.month(startMonth - 1)
							.startOf("month")
							.subtract(1, "years")
					: moment
							.utc()
							.month(startMonth - 1)
							.startOf("month"),
			end: moment.utc()
		},
		"This Quarter": {
			label: "This Quarter",
			start: moment.utc().startOf("quarters"),
			end: moment.utc().endOf("quarters")
		},
		"Last Quarter": {
			label: "Last Quarter",
			start: moment
				.utc()
				.subtract(1, "quarters")
				.startOf("quarters"),
			end: moment
				.utc()
				.subtract(1, "quarters")
				.endOf("quarters")
		},
		"All years to date": {
			label: "All years to date",
			start: moment.utc(minDate).startOf("day"),
			end: moment.utc()
		},
		"All years": {
			label: "All years",
			start: moment.utc(minDate).startOf("day"),
			end: moment.utc(maxDate).startOf("day")
		}
	};

	return wantObj
		? obj
		: {
				startDate: obj[selectedRange].start,
				endDate: obj[selectedRange].end
		  };
};

const getYearStart = (date, propsObject) => {
	const momentDate = moment.utc(date);
	if (propsObject.monthStart === 0 || momentDate.get("month") >= propsObject.monthStart) {
		return momentDate
			.startOf("year")
			.add(propsObject.monthStart, "months")
			.toISOString();
	}
	return momentDate
		.subtract(1, "years")
		.startOf("year")
		.add(propsObject.monthStart, "months")
		.toISOString();
};

const getQuarterStart = (date, propsObject) => {
	const momentDate = moment.utc(date).startOf("day");
	const yearStart = getYearStart(date, propsObject);
	const quarterStartDates = [];
	let initDate = moment.utc(yearStart);
	for (let i = 0; i < 5; i++) {
		quarterStartDates.push(initDate.toISOString());
		initDate = initDate.add(3, "months");
	}
	let result = moment
		.utc(date)
		.startOf("day")
		.toISOString();
	for (let i = 0; i < 4; i++) {
		if (momentDate.isBetween(quarterStartDates[i], quarterStartDates[i + 1], null, "[)")) {
			result = quarterStartDates[i];
			break;
		}
	}
	return result;
};

// dynamic default for date frequency picker
export const getStartEndDateForFrequencyFilter = propsObject => {
	const defaultValue = propsObject.default;
	const { frequency } = defaultValue;
	defaultValue.startDate = moment
		.utc(propsObject.minDate)
		.startOf("day")
		.toISOString();
	switch (frequency) {
		case "yearly":
			defaultValue.endDate = moment
				.utc(getYearStart(moment.utc(), propsObject))
				.add(1, "years")
				.toISOString();
			break;
		case "quarterly":
			defaultValue.endDate = moment
				.utc(getQuarterStart(moment.utc(), propsObject))
				.add(3, "months")
				.toISOString();
			break;
		case "monthly":
			defaultValue.endDate = moment
				.utc()
				.startOf("month")
				.add(1, "months")
				.toISOString();
			break;
		default:
			defaultValue.startDate = null;
			defaultValue.endDate = null;
			defaultValue.compareWith = null;
			break;
	}
	return defaultValue;
};

// Shared normalization function - MUST be used consistently across all drilldown transformations (FusionCharts + Recharts drilldown)
export const normalizeLabelForFusionCharts = label => {
	if (!label) return "";
	return String(label)
		.replace(/\s+/g, "") // Remove all whitespace
		.replace(/[^a-zA-Z0-9]/g, "_"); // Replace ALL non-alphanumeric chars with underscore (FusionCharts-safe)
};

// Manual "Wrap Text" support for X-axis category labels. FusionCharts' `labelDisplay: "wrap"`
// chart attribute is NOT actually implemented as text-wrapping in the bundled fusioncharts
// engine (confirmed by reading node_modules/fusioncharts/fusioncharts.js directly) — it falls
// through to the same code path as "auto" and can only rotate or ellipsis-truncate a label,
// never split it across lines. The library's real (if undocumented) multi-line mechanism is
// literal `<br>` tags embedded in the label text itself (its getSmartText helper has explicit
// <br>-tag handling). So "wrap" has to be done here, on the label STRING, before it ever
// reaches FusionCharts.
//
// Deliberately no character-count threshold: an earlier version only wrapped labels longer
// than 15 characters, but FusionCharts' own "auto" heuristic was ALSO independently deciding
// (based on its own pixel-width overlap math, not this toggle) to wrap some same-length labels
// and not others in the same chart — e.g. "Middle Mgmt" wrapping while "Senior Mgmt" didn't,
// despite being the same length. That's confusing precisely because it's inconsistent. This
// toggle needs to be fully deterministic instead: when enabled, every label with a space
// splits at each space (one line per word) every time, regardless of length, so the whole axis
// wraps uniformly rather than at FusionCharts' own unpredictable discretion.
export const wrapFusionAxisLabel = (label, enabled) => {
	if (!enabled || label == null) return label;
	const text = String(label);
	if (!text.includes(" ")) return text;
	return text.split(" ").filter(Boolean).join("<br>");
};

// Inverse of wrapFusionAxisLabel (and a general HTML-entity decoder) for FusionCharts event
// payloads. FusionCharts' click/hover/right-click event objects report whatever text is
// actually rendered, so when "Wrap Text" is on, categoryLabel/label/name come back as the
// literal "<br>"-joined string (e.g. "Non<br>Mgmt") rather than the original label
// ("Non Mgmt"). Every consumer that displays or matches/filters on this label — cross-filter,
// hover-preview lookups, the Drill By context menu/breadcrumb, and the Drill By/Drill to
// Detail data lookups — must run the label through this first, or it will either show the
// raw "<br>" tag text to the user or use it as a lookup key that never matches data keyed by
// the clean label. Shared here (single source of truth) rather than duplicated per call site.
export const decodeFCLabel = (raw) => {
	if (raw == null) return null;
	return String(raw)
		.replace(/&#(\d+);/g, (_, c) => String.fromCharCode(c))
		.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
		.replace(/<br\s*\/?>/gi, ' ');
};

export const singleSeriesTranformationFunction = (responseData, dataObj, propsObject) => {
	const hasDrilldown =
		(dataObj[0].drilldownSeriesList && Array.isArray(dataObj[0].drilldownSeriesList) && dataObj[0].drilldownSeriesList.length > 0) ||
		(dataObj[0].drilldownSeries && dataObj[0].drilldownSeries);
	const xAxisLabelWrapEnabled = propsObject && propsObject.xAxisLabelWrap === "1";

	const xSeriesKey = dataObj[0].seriesOn[0];
	const result = responseData[0].map(item => {
		const label = transformDateTypeData(item[xSeriesKey]);
		// Normalize label for link (MUST match normalization in linkedData transformation) —
		// derived from the unwrapped label, same as always; only the displayed label wraps.
		const normalizedLabel = hasDrilldown ? normalizeLabelForFusionCharts(label) : null;
		const link = hasDrilldown ? `newchart-json-${normalizedLabel}` : null;

		return {
			label: wrapFusionAxisLabel(label, xAxisLabelWrapEnabled),
			value: item.value,
			link,
			// Include raw dimension value for drill-by (Fusion sends display label; API expects raw e.g. fy_timerange)
			[xSeriesKey]: item[xSeriesKey]
		};
	});

	return result;
};

export const singleSeriesLinkedDataTransformation = (
	responseData,
	{ data: dataObj, props },
	parentData
) => {
	const parentKey = dataObj[0].seriesOn[0];

	const baseDrilldownList =
		Array.isArray(dataObj[0].drilldownSeriesList)
			? dataObj[0].drilldownSeriesList
			: dataObj[0].drilldownSeries
			? [dataObj[0].drilldownSeries]
			: [];
	// Per-level sort order set in the "Add drilldowns" panel, index-aligned with
	// baseDrilldownList. A falsy entry means "leave in original/insertion order".
	const baseDrilldownSortOrders = Array.isArray(dataObj[0].drilldownSortOrders)
		? dataObj[0].drilldownSortOrders
		: [];

	const normalize = normalizeLabelForFusionCharts;
	const baseChartProps = { ...props, theme: "fusion" };
	const xAxisLabelWrapEnabled = props && props.xAxisLabelWrap === "1";

	/* ---------------- SINGLE LEVEL (Backward Compatible) ---------------- */

	if (baseDrilldownList.length <= 1) {
		const result = {};
		const labelKey = baseDrilldownList[0];

		for (const item of responseData[0]) {
			const parentLabel = transformDateTypeData(item[parentKey]);
			const label = transformDateTypeData(item[labelKey]);

			if (!result[parentLabel]) {
				result[parentLabel] = {
					id: normalize(parentLabel),
					linkedchart: {
						chart: baseChartProps,
						data: []
					}
				};
			}

			result[parentLabel].linkedchart.data.push({
				label: wrapFusionAxisLabel(label, xAxisLabelWrapEnabled),
				value: item.value
			});
		}

		const sortOrder = baseDrilldownSortOrders[0];
		if (sortOrder) {
			Object.values(result).forEach(entry => {
				entry.linkedchart.data = applySortOrder(
					entry.linkedchart.data.map(d => ({ name: d.label, value: d.value })),
					sortOrder
				).map(d => ({ label: d.name, value: d.value }));
			});
		}
		return Object.values(result);
	}

	/* ---------------- MULTI LEVEL (FUSION SAFE) ---------------- */

	// Use all configured drilldown levels (no hard cap)
	const drilldownList = baseDrilldownList;

	// Build hierarchy tree
	const tree = {};
	for (const item of responseData[0]) {
		const parentLabel = transformDateTypeData(item[parentKey]);
		tree[parentLabel] ??= { children: {} };

		let node = tree[parentLabel];
		drilldownList.forEach(key => {
			const label = transformDateTypeData(item[key]);
			node.children[label] ??= { children: {} };
			node = node.children[label];
		});

		node.value = (node.value || 0) + item.value;
	}

	const sumNode = node =>
		Object.keys(node.children).length === 0
			? node.value || 0
			: Object.values(node.children).reduce((s, c) => s + sumNode(c), 0);

	const makeId = path => path.map(normalize).join("__");
	const makeLink = path => `newchart-json-${makeId(path)}`;

	// Root level linkedData array (first level only)
	const linkedData = [];

	// Recursive function to build linkedchart with nested linkeddata
	const buildLinkedChart = (node, path, levelIndex) => {
		let children = Object.keys(node.children);
		if (!children.length) return null;

		const sortOrder = baseDrilldownSortOrders[levelIndex];
		if (sortOrder) {
			const sortable = children.map(label => ({ name: label, value: sumNode(node.children[label]) }));
			children = applySortOrder(sortable, sortOrder).map(item => item.name);
		}

		const data = children.map(label => {
			const child = node.children[label];
			const childPath = [...path, label];
			const hasDeeper =
				Object.keys(child.children).length > 0 &&
				levelIndex + 1 < drilldownList.length;

			// childPath/makeLink use the raw `label` (tree key / id component) unchanged —
			// only the label field actually rendered by the chart gets wrapped.
			return {
				label: wrapFusionAxisLabel(label, xAxisLabelWrapEnabled),
				value: sumNode(child),
				...(hasDeeper ? { link: makeLink(childPath) } : {})
			};
		});

		// Build nested linkeddata for deeper levels
		const nestedLinkedData = [];
		children.forEach(label => {
			const child = node.children[label];
			if (
				Object.keys(child.children).length > 0 &&
				levelIndex + 1 < drilldownList.length
			) {
				const childLinkedChart = buildLinkedChart(
					child,
					[...path, label],
					levelIndex + 1
				);
				if (childLinkedChart) {
					nestedLinkedData.push(childLinkedChart);
				}
			}
		});

		return {
			id: makeId(path),
			linkedchart: {
				chart: baseChartProps,
				data,
				...(nestedLinkedData.length > 0 ? { linkeddata: nestedLinkedData } : {})
			}
		};
	};

	// Build first level linkedcharts (these go in root linkedData array)
	Object.keys(tree).forEach(parentLabel => {
		const parentNode = tree[parentLabel];
		const firstLevelChart = buildLinkedChart(parentNode, [parentLabel], 0);
		if (firstLevelChart) {
			linkedData.push(firstLevelChart);
		}
	});

	return linkedData;
};

// Same shape as singleSeriesLinkedDataTransformation, but each drilldown level keeps
// one value per measure (categories/dataset) instead of collapsing to a single value,
// so stacked/combo/clustered FC charts stay stacked/combo/clustered after drilling.
export const multiSeriesLinkedDataTransformation = (
	responseData,
	{ data: dataObj, props },
	parentData
) => {
	const parentKey = dataObj[0].seriesOn[0];

	const baseDrilldownList =
		Array.isArray(dataObj[0].drilldownSeriesList)
			? dataObj[0].drilldownSeriesList
			: dataObj[0].drilldownSeries
			? [dataObj[0].drilldownSeries]
			: [];
	// Per-level sort order set in the "Add drilldowns" panel, index-aligned with
	// baseDrilldownList. A falsy entry means "leave in original/insertion order".
	const baseDrilldownSortOrders = Array.isArray(dataObj[0].drilldownSortOrders)
		? dataObj[0].drilldownSortOrders
		: [];

	if (baseDrilldownList.length === 0) return [];

	const normalize = normalizeLabelForFusionCharts;
	const baseChartProps = { ...props, theme: "fusion" };
	const xAxisLabelWrapEnabled = props && props.xAxisLabelWrap === "1";
	const seriesNames = dataObj.map((measureObj, index) => measureObj.label || `series${index + 1}`);
	const drilldownList = baseDrilldownList;

	// Build hierarchy tree; each node holds per-series sums at its own level (node.series[seriesName])
	const tree = {};
	dataObj.forEach((measureObj, measureIndex) => {
		const rows = responseData[measureIndex] || [];
		const seriesName = seriesNames[measureIndex];
		for (const item of rows) {
			const parentLabel = transformDateTypeData(item[parentKey]);
			tree[parentLabel] ??= { children: {} };

			let node = tree[parentLabel];
			drilldownList.forEach(key => {
				const label = transformDateTypeData(item[key]);
				node.children[label] ??= { children: {} };
				node = node.children[label];
			});

			node.series ??= {};
			const v = typeof item.value === "number" ? item.value : parseFloat(item.value);
			node.series[seriesName] = (node.series[seriesName] || 0) + (isNaN(v) ? 0 : v);
		}
	});

	const sumSeries = (node, seriesName) =>
		Object.keys(node.children).length === 0
			? (node.series && node.series[seriesName]) || 0
			: Object.values(node.children).reduce((s, c) => s + sumSeries(c, seriesName), 0);

	const makeId = path => path.map(normalize).join("__");
	const makeLink = path => `newchart-json-${makeId(path)}`;

	const linkedData = [];

	// Recursive function to build a categories/dataset linkedchart level, mirroring
	// buildLinkedChart above but carrying one dataset entry per measure/series.
	const buildLinkedChart = (node, path, levelIndex) => {
		let childLabels = Object.keys(node.children);
		if (!childLabels.length) return null;

		const sortOrder = baseDrilldownSortOrders[levelIndex];
		if (sortOrder) {
			const sortable = childLabels.map(label => {
				const child = node.children[label];
				const total = seriesNames.reduce((sum, seriesName) => sum + sumSeries(child, seriesName), 0);
				return { name: label, value: total };
			});
			childLabels = applySortOrder(sortable, sortOrder).map(item => item.name);
		}

		// childLabels themselves stay raw (used below for makeLink/tree lookups) — only the
		// displayed category label text gets wrapped.
		const categories = [{ category: childLabels.map(label => ({ label: wrapFusionAxisLabel(label, xAxisLabelWrapEnabled) })) }];
		// Preserve each series' configured plot type (bar/line) and Y-axis assignment from the
		// Series Configuration panel — mirrors MultiSeriesCharts.js's applyComboDataset, which
		// only ever runs on the ROOT chart's dataset. Without this, every drilled-in level
		// dataset entry came back with neither `renderas` nor `parentYAxis` set at all, so
		// FusionCharts fell back to its own default (every series as a bar, all on the primary
		// axis) — losing the root's bar+line dual-axis combo the moment the user drilled in,
		// and also (since two series-as-bars need roughly double the per-category width of one
		// bar + one line) very likely making the drilled-in category-label clipping worse too.
		const dataset = seriesNames.map(seriesName => {
			const renderas = props?.types?.[seriesName];
			const axisVal = props?.axes?.[seriesName];
			const parentYAxis = axisVal === "y2" ? "S" : axisVal ? "P" : undefined;
			return {
				seriesname: seriesName,
				...(renderas ? { renderas } : {}),
				...(parentYAxis !== undefined ? { parentYAxis } : {}),
				data: childLabels.map(label => {
					const child = node.children[label];
					const hasDeeper =
						Object.keys(child.children).length > 0 && levelIndex + 1 < drilldownList.length;
					return {
						value: sumSeries(child, seriesName),
						...(hasDeeper ? { link: makeLink([...path, label]) } : {})
					};
				})
			};
		});

		const nestedLinkedData = [];
		childLabels.forEach(label => {
			const child = node.children[label];
			if (Object.keys(child.children).length > 0 && levelIndex + 1 < drilldownList.length) {
				const childLinkedChart = buildLinkedChart(child, [...path, label], levelIndex + 1);
				if (childLinkedChart) {
					nestedLinkedData.push(childLinkedChart);
				}
			}
		});

		return {
			id: makeId(path),
			linkedchart: {
				chart: baseChartProps,
				categories,
				dataset,
				...(nestedLinkedData.length > 0 ? { linkeddata: nestedLinkedData } : {})
			}
		};
	};

	Object.keys(tree).forEach(parentLabel => {
		const firstLevelChart = buildLinkedChart(tree[parentLabel], [parentLabel], 0);
		if (firstLevelChart) {
			linkedData.push(firstLevelChart);
		}
	});

	return linkedData;
};

/**
 * Reshape generic multi-series linkeddata into the structure msstackedcolumn2dlinedy
 * (Dual Axis Stacked Column & Line) actually renders.
 *
 * multiSeriesLinkedDataTransformation emits one FLAT `dataset` array per drilldown level,
 * which is what every other multi-series FusionCharts type expects. msstackedcolumn2dlinedy
 * is stricter: unlike the plain stackedcolumn2dlinedy chart FusionCharts documents a flat
 * dataset shape for, the "ms" (multi-series) variant only ever accepts column series nested
 * into stacked groups (`dataset: [{ dataset: [...] }]`) with any line series moved out into a
 * sibling `lineset` — a flat dataset (even with renderAs/parentYAxis stamped per item) renders
 * as "No data to display." regardless of Series Configuration, confirmed by feeding both shapes
 * directly into a live FusionCharts instance. MultiSeriesCharts.js does this partitioning for
 * the ROOT chart, but nothing did it for the drilled-in levels — so drilling in showed
 * "No data to display" at every level.
 *
 * Mirrors MultiSeriesCharts.js's grouping so root and drilldown agree: columns are always
 * chunked into stacked groups of two (split further into P/S groups when axes are configured),
 * and line series always move to `lineset` with their resolved axis assignment. `typeOf`/
 * `resolveAxis` already consult the `types`/`axes` config, so there is no separate
 * "series config present" case to special-case.
 *
 * @param {Array}  linkedData - output of multiSeriesLinkedDataTransformation
 * @param {Object} props      - the widget's styling props (carries `types` and `axes`)
 * @returns {Array} new linkeddata array; originals are not mutated
 */
export const reshapeStackedColumnLineLinkedData = (linkedData, props) => {
	if (!Array.isArray(linkedData) || linkedData.length === 0) return linkedData;

	const types = (props && props.types) || {};
	const axes = (props && props.axes) || {};
	const SERIES_PER_STACK_GROUP = 2;

	const resolveAxis = (series, seriesType) => {
		const axisKey = axes[series.seriesname];
		if (axisKey === "y2") return "S";
		if (axisKey) return "P";
		if (series.parentYAxis) return series.parentYAxis;
		return seriesType === "line" ? "S" : "P";
	};

	const reshapeLevel = chart => {
		const flat = Array.isArray(chart.dataset) ? chart.dataset : [];
		// Already nested (a group carries its own `dataset`) — leave it alone.
		if (flat.length === 0 || (flat[0] && Array.isArray(flat[0].dataset))) return chart;

		const allSeries = [...flat, ...(Array.isArray(chart.lineset) ? chart.lineset : [])];
		const typeOf = series =>
			String(types[series.seriesname] || series.renderas || series.renderAs || "bar").toLowerCase();

		// Columns chunked into stacked groups of two, line series moved to lineset.
		const columns = [];
		const lines = [];
		allSeries.forEach(series => {
			const seriesType = typeOf(series);
			const parentYAxis = resolveAxis(series, seriesType);
			if (seriesType === "line") {
				const lineSeries = { ...series, parentYAxis };
				delete lineSeries.renderas;
				delete lineSeries.renderAs;
				lines.push(lineSeries);
			} else {
				columns.push({ ...series, parentYAxis });
			}
		});

		const dataset = [];
		const y1 = columns.filter(s => s.parentYAxis !== "S");
		const y2 = columns.filter(s => s.parentYAxis === "S");
		for (let i = 0; i < y1.length; i += SERIES_PER_STACK_GROUP) {
			dataset.push({ dataset: y1.slice(i, i + SERIES_PER_STACK_GROUP) });
		}
		for (let i = 0; i < y2.length; i += SERIES_PER_STACK_GROUP) {
			dataset.push({ dataset: y2.slice(i, i + SERIES_PER_STACK_GROUP), parentYAxis: "S" });
		}

		return { ...chart, dataset, ...(lines.length > 0 ? { lineset: lines } : {}) };
	};

	const walk = entries =>
		entries.map(entry => {
			if (!entry || !entry.linkedchart) return entry;
			const reshaped = { ...entry, linkedchart: reshapeLevel(entry.linkedchart) };
			if (Array.isArray(entry.linkedchart.linkeddata) && entry.linkedchart.linkeddata.length > 0) {
				reshaped.linkedchart = {
					...reshaped.linkedchart,
					linkeddata: walk(entry.linkedchart.linkeddata)
				};
			}
			return reshaped;
		});

	try {
		return walk(linkedData);
	} catch (_) {
		return linkedData;
	}
};


// Parses time-period style labels ("Apr'23", the raw ISO pipe-range shape handled by
// isISOString, "2024-01", "2024", "FY2024") into a comparable timestamp, or returns null
// when the value isn't recognizably a date/time label — used by getSortedResult so a
// genuinely chronological field sorts by date instead of alphabetically by its label text
// (e.g. "Apr'23" sorting before "Aug'23" purely because "A" < "A" and "p" < "u").
const parseTimeSortValue = value => {
	const s = typeof value === "string" ? value.trim() : "";
	if (!s) return null;
	if (isISOString(s)) {
		const [startDate] = s.split("|");
		const ts = moment.utc(startDate).valueOf();
		return Number.isNaN(ts) ? null : ts;
	}
	// Lenient fallback for pipe-range values that don't exactly match isISOString's strict
	// millisecond/"Z"-suffixed pattern (e.g. no milliseconds, a "+00:00" offset) — moment can
	// still parse the leading date portion even when the exact regex above doesn't match it.
	if (s.includes("|")) {
		const [startDate] = s.split("|");
		const parsed = moment.utc(startDate);
		if (parsed.isValid()) return parsed.valueOf();
	}
	// "Apr'23", "Apr 2023", "Apr-23" (also accepts the curly/smart-quote apostrophe some
	// upstream formatting produces, U+2019, in addition to the plain ASCII apostrophe)
	const monthYear = s.match(/^([A-Za-z]{3,})['’.\s-]+(\d{2,4})$/);
	if (monthYear) {
		const parsed = moment.utc(`${monthYear[1]} ${monthYear[2]}`, ["MMM YY", "MMM YYYY", "MMMM YY", "MMMM YYYY"], true);
		if (parsed.isValid()) return parsed.valueOf();
	}
	// "2024-01", "2024-01-15", "2024"
	if (/^\d{4}(-\d{2}(-\d{2})?)?$/.test(s)) {
		const parsed = moment.utc(s, ["YYYY-MM-DD", "YYYY-MM", "YYYY"], true);
		if (parsed.isValid()) return parsed.valueOf();
	}
	return null;
};

const getSortedResult = (sortBy = "", sortOrder = "ASC", data = [[]]) => {
	const dataToModify = data.filter(arr => !_.isEqual(arr, data[0]));
	if (data[0][0] === sortBy || (typeof sortBy === "object" && Object.keys(sortBy)[0] === data[0][0])) {
		sortOrder =
			sortBy && typeof sortBy === "object" && sortBy[Object.keys(sortBy)[0]].sortOrder
				? sortBy[Object.keys(sortBy)[0]].sortOrder
				: sortOrder;
		// Decide the comparison strategy ONCE for the whole array rather than per-pair: mixing
		// numeric date-compare and string-compare within the same sort() call produces a
		// non-transitive comparator (undefined behavior), which is exactly what caused a couple
		// of unparsed labels to get dumped at the wrong end instead of merely falling back to
		// string order for the whole array.
		const timeValues = dataToModify.map(row => parseTimeSortValue(row[0]));
		const allParsed = timeValues.length > 0 && timeValues.every(t => t !== null);
		const withTimeValues = dataToModify.map((row, i) => ({ row, t: timeValues[i] }));
		const sorted = withTimeValues.sort((a, b) => {
			let arr1 = a;
			let arr2 = b;
			if (sortOrder === "DESC") {
				arr1 = b;
				arr2 = a;
			}
			if (allParsed) return arr1.t - arr2.t;
			return arr1.row[0] > arr2.row[0] ? 1 : -1;
		});
		return sorted.map(({ row }) => row);
	}
	return dataToModify;
};

const maintainSecondaryOrder = (table, secondaryOrder, dataObject) => {
	const tableOrder = table[0].filter(arrItem => arrItem !== table[0][0]);
	const modifiedTable = [];
	if (!secondaryOrder) {
		secondaryOrder = getSecondarySortLabelOrder(tableOrder, dataObject);
	}
	const indices = [];
	secondaryOrder.forEach((label, index) => {
		tableOrder.indexOf(label) >= 0 && indices.push(tableOrder.indexOf(label));
	});
	for (let arrElem = 0; arrElem < table.length; arrElem++) {
		indices.forEach(indexValue => {
			if (!modifiedTable[arrElem]) {
				modifiedTable[arrElem] = [table[arrElem][0]];
			}
			modifiedTable[arrElem].push(table[arrElem][indexValue + 1]);
		});
	}
	return !modifiedTable.length || !modifiedTable[0] ? [[]] : modifiedTable;
};

const getSecondarySortLabelOrder = (tableOrder, dataObj) => {
	let updatedTableOrder = JSON.parse(JSON.stringify(tableOrder));
	if (dataObj && dataObj[0].sortBy && Object.keys(dataObj[0].sortBy).length) {
		const secondaryDimension = Object.keys(dataObj[0].sortBy)[1];
		if (dataObj[0].seriesOn.includes(secondaryDimension)) {
			updatedTableOrder = updatedTableOrder.sort((a, b) => {
				let label1 = a;
				let label2 = b;

				if (dataObj[0].sortBy[secondaryDimension].sortOrder === "DESC") {
					label1 = b;
					label2 = a;
				}
				return label1 ? (label1 > label2 ? 1 : -1) : 0;
			});
		}
	}
	return updatedTableOrder;
};

export const isISOString = string => {
	if (
		typeof string === "string" &&
		string.match(
			/(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2}).(\d{3})Z\|(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2}).(\d{3})Z/
		)
	) {
		return true;
	}
	return false;
};

export const transformDateTypeData = string => {
	if (string && isISOString(string)) {
		let [startDate, endDate] = string.split("|");
		if (startDate != endDate) {
			endDate = moment(endDate).subtract(1, "ms");
		}
		const difference = moment.utc(endDate).diff(startDate, "month", true);
		if (difference > 11.99 && difference < 12) {
			string = `FY (${moment.utc(startDate).format("YYYY")} - ${moment.utc(endDate).format("YYYY")})`;
		} else if (difference >= 0 && difference < 1) {
			string = moment.utc(startDate).format("MMM'YY");
		} else {
			string = `${moment.utc(startDate).format("MMM'YY")} - ${moment.utc(endDate).format("MMM'YY")}`;
		}
	}
	return string;
};

export const multiSeriesTranformationFunction = (responseData, dataObj, propsObject) => {
	const updatedDataObj = JSON.parse(JSON.stringify(dataObj));
	let table = [];
	if (updatedDataObj && updatedDataObj[0].breakdownSelected) {
		const temporarytTable = getBreakDownTable(responseData, updatedDataObj);
		table = maintainSecondaryOrder(temporarytTable, undefined, dataObj);
	} else {
		const temporarytTable = getTable(responseData, updatedDataObj);
		const secondaryOrder = dataObj.map(obj => obj.label);
		table = maintainSecondaryOrder(temporarytTable, secondaryOrder);
	}
	const result = {
		categories: [],
		dataset: []
	};
	const labels = [];
	const tableLabels = table[0];
	table = getSortedResult(dataObj[0].sortBy, dataObj[0].sortOrder, table);
	table.unshift(tableLabels);
	const xAxisLabelWrapEnabled = propsObject && propsObject.xAxisLabelWrap === "1";
	for (let i = 1; i < table.length; i++) {
		labels.push({
			label: wrapFusionAxisLabel(transformDateTypeData(table[i][0]), xAxisLabelWrapEnabled)
		});
	}
	result.categories.push({
		category: labels
	});

	// Native FusionCharts click-drilldown: each data point needs a `link` pointing to the
	// matching linkeddata entry's id (built by multiSeriesLinkedDataTransformation using the
	// same normalized category label) - without this FusionCharts has nothing to navigate to
	// on click, even though the linkeddata array itself is present (mirrors the `link` that
	// singleSeriesTranformationFunction already attaches for single-series widgets).
	const hasDrilldown =
		(dataObj[0].drilldownSeriesList && Array.isArray(dataObj[0].drilldownSeriesList) && dataObj[0].drilldownSeriesList.length > 0) ||
		(dataObj[0].drilldownSeries && dataObj[0].drilldownSeries);

	if (table[0]) {
		for (let i = 1; i < table[0].length; i++) {
			const temp = {};
			temp.seriesname = transformDateTypeData(table[0][i]);
			if (propsObject && typeof propsObject.colorPalette === "object") {
				temp.color =
					propsObject.colorPalette[transformDateTypeData(table[0][i])] ||
					propsObject.colorPalette[
						`${updatedDataObj[0].label}-${transformDateTypeData(table[0][i])}`
					];
			}
			temp.data = [];
			for (let j = 1; j < table.length; j++) {
				const point = { value: table[j][i] };
				if (hasDrilldown) {
					point.link = `newchart-json-${normalizeLabelForFusionCharts(transformDateTypeData(table[j][0]))}`;
				}
				temp.data.push(point);
			}
			result.dataset.push(temp);
		}
	}
	return result;
};

export const speedometerDataTransformationFunction = responseData => {
	const result = {
		value: 0
	};
	if (responseData.length == 2) {
		result.value = parseFloat(responseData[1] / responseData[0]);
	}
	return result;
};

export const bulletDataTransformationFunction = responseData => {
	const result = {};
	if (responseData.length == 2) {
		result.target = responseData[0];
		result.value = responseData[1];
	}
	return result;
};

export const googleChartsTransformationFunction = (responseData, dataObj, propsObject) => {
	return getTable(responseData, dataObj);
};

export const inputOutputCardTransformationFunction = (responseData, dataObj, propsObject) => {
	const result = JSON.parse(JSON.stringify(responseData));
	for (var i in dataObj) {
		if (!dataObj[i].label) {
			dataObj[i].label = `Label ${i}`;
		}
	}
	const table = [["inputText", "inputVal", "outputText", "outputVal"]];
	let row = [];
	for (var i = 0; i < result.length; i++) {
		row.push(dataObj[i].label);
		row.push(result[i]);
		if (row.length == 4) {
			table.push(row);
			row = [];
		}
	}
	const data = [];
	if (table.length > 0) {
		for (var i = 0; i < table.length - 1; i++) {
			const temp = {};
			for (let j = 0; j < table[0].length; j++) {
				temp[table[0][j]] = table[i + 1][j];
			}
			data.push(temp);
		}
	}
	return data;
};

export const bubbleDataTransformationFunction = (responseData, dataObj, propsObject) => {
	let table = getTable(responseData, dataObj);
	const result = {
		categories: [],
		dataset: []
	};
	const xAxisLabelWrapEnabled = propsObject && propsObject.xAxisLabelWrap === "1";
	const labels = [];
	const tableLabels = table[0];
	table = getSortedResult(dataObj[0].sortBy, dataObj[0].sortOrder, table);
	table.unshift(tableLabels);
	for (let i = 1; i < table.length; i++) {
		if (table[i][0] || table[i][0] == "0") {
			const rawLabel = transformDateTypeData(table[i][0]);
			labels.push({
				// x stays raw — it's the category key FusionCharts matches against dataset
				// entries; only `label` (the displayed axis tick text) gets wrapped.
				label: wrapFusionAxisLabel(rawLabel, xAxisLabelWrapEnabled),
				x: rawLabel
			});
		}
	}
	result.categories.push({
		category: labels
	});
	const dataArray = [];
	for (let i = 1; i < table.length; i++) {
		const temp = {
			x: transformDateTypeData(table[i][1]),
			y: transformDateTypeData(table[i][2]),
			z: transformDateTypeData(table[i][3]),
			name: transformDateTypeData(table[i][0]) || ""
		};
		dataArray.push(temp);
	}
	result.dataset.push({
		data: dataArray
	});
	if (propsObject.showverticalline) {
		for (const i in result.categories[0].category) {
			result.categories[0].category[i].showverticalline = propsObject.showverticalline;
		}
	}
	return result;
};

export const scatterDataTransformationFunction = (responseData, dataObj, propsObject) => {
	let table = getTable(responseData, dataObj);
	const result = {
		categories: [],
		dataset: []
	};
	const xAxisLabelWrapEnabled = propsObject && propsObject.xAxisLabelWrap === "1";
	const labels = [];
	const tableLabels = table[0];
	table = getSortedResult(dataObj[0].sortBy, dataObj[0].sortOrder, table);
	table.unshift(tableLabels);

	// Handle multiple indicators/series for scatter chart
	// Expected format: dimension, x1, y1, x2, y2, ... (pairs of x,y values)
	const numIndicators = Math.floor((table[0].length - 1) / 2);

	if (numIndicators > 0) {
		// Create labels from dimension column
		for (let i = 1; i < table.length; i++) {
			if (table[i][0] || table[i][0] == "0") {
				const rawLabel = transformDateTypeData(table[i][0]);
				labels.push({
					// x stays raw — it's the category key FusionCharts matches against dataset
					// entries; only `label` (the displayed axis tick text) gets wrapped.
					label: wrapFusionAxisLabel(rawLabel, xAxisLabelWrapEnabled),
					x: rawLabel
				});
			}
		}
		result.categories.push({
			category: labels
		});
		
		// Create a dataset for each x,y pair (series)
		for (let seriesIdx = 0; seriesIdx < numIndicators; seriesIdx++) {
			const xColIdx = 1 + (seriesIdx * 2);
			const yColIdx = 2 + (seriesIdx * 2);
			
			// Check if columns exist
			if (xColIdx < table[0].length && yColIdx < table[0].length) {
				const dataArray = [];
				const xLabel = table[0][xColIdx] || `X${seriesIdx + 1}`;
				const yLabel = table[0][yColIdx] || `Y${seriesIdx + 1}`;
				
				// Get series name from indicator labels, or construct from x,y labels
				let seriesName = dataObj[xColIdx - 1]?.label || yLabel;
				
				for (let i = 1; i < table.length; i++) {
					const xVal = table[i][xColIdx];
					const yVal = table[i][yColIdx];
					
					// Only add data point if both x and y values exist
					if ((xVal !== null && xVal !== undefined && xVal !== "") || 
					    (yVal !== null && yVal !== undefined && yVal !== "")) {
						dataArray.push({
							x: transformDateTypeData(xVal),
							y: transformDateTypeData(yVal)
						});
					}
				}
				
				// Only add dataset if it has data
				if (dataArray.length > 0) {
					const dataset = {
						seriesname: transformDateTypeData(seriesName),
						data: dataArray
					};
					
					// Determine if individual anchor colors are explicitly set (not empty/undefined)
					const hasIndividualBgColor = propsObject && propsObject.anchorBgColor && propsObject.anchorBgColor.trim() !== '';
					const hasIndividualBorderColor = propsObject && propsObject.anchorBorderColor && propsObject.anchorBorderColor.trim() !== '';
					
					// Apply color palette if provided
					if (propsObject && typeof propsObject.colorPalette === "object") {
						dataset.color = propsObject.colorPalette[seriesName] || 
						               propsObject.colorPalette[`${dataObj[0]?.label}-${seriesName}`];
					}
					// Apply colors from palettecolors string (comma-separated)
					// For scatter charts: first color = anchor background, second color = anchor border
					else if (propsObject && propsObject.palettecolors) {
						const colors = propsObject.palettecolors.split(',').map(c => c.trim());
						const colorIndex = seriesIdx % colors.length;
						const seriesColor = colors[colorIndex];
						dataset.color = seriesColor;
						
						// Apply palette colors to anchors ONLY if individual colors are not set
						if (!hasIndividualBgColor) {
							dataset.anchorbgcolor = colors[0] || seriesColor;
						}
						if (!hasIndividualBorderColor) {
							dataset.anchorbordercolor = colors.length > 1 ? colors[1] : colors[0];
						}
					}
					
					// Apply individual anchor colors if explicitly set (these override palette colors)
					if (hasIndividualBgColor) {
						dataset.anchorbgcolor = propsObject.anchorBgColor;
					}
					if (hasIndividualBorderColor) {
						dataset.anchorbordercolor = propsObject.anchorBorderColor;
					}
					
					result.dataset.push(dataset);
				}
			}
		}
	}
	
	// Apply vertical line settings if provided
	if (propsObject && propsObject.showverticalline && result.categories[0]) {
		for (const i in result.categories[0].category) {
			result.categories[0].category[i].showverticalline = propsObject.showverticalline;
		}
	}
	
	return result;
};

export const heatMapTranformationFunction = (responseData, dataObj, propsObject) => {
	const table = getTable(responseData, dataObj);
	const result = {
		stateArr: []
	};
	if (table && table.length) {
		result.primaryAttribute = propsObject.primaryAttribute || "";
		result.displayAttributes = propsObject.displayAttributes || [];
		result.formatter = [];
		for (var i = 1; i < table.length; i++) {
			const obj = {};
			for (let j = 0; j < table[i].length; j++) {
				if (j == 0) {
					obj.stateId = table[i][j];
				} else {
					obj[table[0][j]] = table[i][j];
				}
			}
			result.stateArr.push(obj);
		}
		for (var i = 0; i < dataObj.length; i++) {
			result.formatter[dataObj[i].label] = (({
				scaling,
				currency,
				decimalPlaces,
				negativeNoFormat,
				region
			}) => ({ scaling, currency, decimalPlaces, negativeNoFormat, region }))(dataObj[i]);
		}
	}

	return result;
};

export const treeMapTransformationFunction = (responseData, dataObj, propsObject) => {
	const result = JSON.parse(JSON.stringify(responseData));
	const keys = [];
	let table = [];
	for (var i in dataObj) {
		if (!dataObj[i].label) {
			dataObj[i].label = `Label ${i}`;
		}
		if (dataObj[i].seriesOn) {
			for (var j in dataObj[i].seriesOn) {
				if (keys.indexOf(dataObj[i].seriesOn[j]) < 0) {
					keys.push(dataObj[i].seriesOn[j]);
				}
			}
		}
	}
	for (var i in result) {
		for (var j in result[i]) {
			const value = parseFloat(result[i][j].value);
			result[i][j][dataObj[i].label] = value;
			delete result[i][j].value;
		}
	}
	if (keys.length == 2 && dataObj.length == 1 && dataObj[0].seriesOn.length == 2) {
		table = create2DArray(result, keys);
	}
	if (table && table.length) {
		let sum = 0;
		for (var i = 1; i < table.length; i++) {
			sum += table[i][2];
		}
		table[0][3] = "color";
		for (var i = 1; i < table.length; i++) {
			table[i][3] = Math.floor(((sum - table[i][2]) / sum) * 100);
		}
	}
	return table;
};

const getTable = (responseData, dataObj) => {
	const keys = dataObj[0].seriesOn;
	const primarySort = dataObj[0].primarySortBy;
	const { primarySortOrder } = dataObj[0];
	const actualDims = dataObj[0].actualDimensions;

	// If a sort dimension was added to seriesOn (primarySortBy ∉ actualDimensions),
	// pre-aggregate the response by actualDimensions so FusionCharts doesn't receive
	// multiple rows per x-axis label (e.g. 12 monthly rows instead of 1 yearly row).
	// The API already returns rows in sort order, so insertion order = correct order.
	let effectiveResponseData = responseData;
	if (
		primarySort &&
		Array.isArray(actualDims) &&
		actualDims.length > 0 &&
		actualDims.indexOf(primarySort) === -1
	) {
		effectiveResponseData = responseData.map(dataSlot => {
			if (!Array.isArray(dataSlot)) return dataSlot;
			const aggMap = new Map();
			const order = [];
			dataSlot.forEach(row => {
				const dimKey = actualDims.map(d => (row[d] !== undefined ? row[d] : '')).join('|');
				if (aggMap.has(dimKey)) {
					const existing = aggMap.get(dimKey);
					Object.keys(row).forEach(k => {
						if (actualDims.indexOf(k) === -1 && typeof row[k] === 'number') {
							existing[k] = (existing[k] || 0) + row[k];
						}
					});
				} else {
					const copy = Object.assign({}, row);
					aggMap.set(dimKey, copy);
					order.push(dimKey);
				}
			});
			return order.map(k => aggMap.get(k));
		});
	}

	const updateResponseData = JSON.parse(JSON.stringify(effectiveResponseData));
	for (const i in updateResponseData) {
		for (const j in updateResponseData[i]) {
			const value = parseFloat(updateResponseData[i][j].value);
			updateResponseData[i][j][dataObj[i].label] = value;
			delete updateResponseData[i][j].value;
		}
	}
	return create2DArray(updateResponseData, actualDims && actualDims.length > 0 && primarySort && actualDims.indexOf(primarySort) === -1 ? actualDims : keys, primarySort, primarySortOrder);
};

const getBreakDownTable = (responseData, dataObj) => {
	if (dataObj && dataObj[0] && dataObj[0].seriesOn && dataObj[0].seriesOn.length) {
		const primaryDimension = dataObj[0].seriesOn[0];
		const secondaryDimension = dataObj[0].seriesOn[1] || "key";
		const secArr = {};
		const primaryArr = {};
		let count = 1;
		let primaryCount = 1;
		const chartData = [[primaryDimension]];
		if (primaryDimension) {
			for (const i in responseData[0]) {
				const pd = responseData[0][i][primaryDimension];
				const sd = responseData[0][i][secondaryDimension];
				const { value } = responseData[0][i];
				if (chartData[0].indexOf(sd) === -1) {
					chartData[0].push(sd);
					secArr[sd] = count++;
				}
				if (!primaryArr[pd]) {
					primaryArr[pd] = primaryCount++;
				}
				if (!(chartData[primaryArr[pd]] && chartData.length)) {
					chartData.push([]);
				}
				chartData[primaryArr[pd]][0] = pd;
				chartData[primaryArr[pd]][secArr[sd]] = value;
			}
		}
		return chartData;
	}
	return [];
};

const scaleMapping = {
	None: {
		suffix: "",
		denominator: 1
	},
	Percent: {
		suffix: "%",
		denominator: 1
	},
	Thousand: {
		suffix: "K",
		denominator: 1000
	},
	Lakh: {
		suffix: "L",
		denominator: 100000
	},
	Crore: {
		suffix: "Cr",
		denominator: 10000000
	},
	Million: {
		suffix: "M",
		denominator: 1000000
	},
	Billion: {
		suffix: "B",
		denominator: 1000000000
	},
	Trillion: {
		suffix: "T",
		denominator: 1000000000000
	}
};

const NumberFormatMap = {
	India: {
		code: "en-IN"
	},
	"US/UK": {
		code: "en-US"
	},
	Asia: {
		code: "en-US"
	},
	Europe: {
		code: "de-DE"
	},
	Sweden: {
		code: "ru-RU"
	}
};

const convertExponentialToDecimal = exponentialNumber => {
	const str = exponentialNumber.toString();
	if (str.indexOf("e") !== -1) {
		const exponent = parseInt(str.split("-")[1], 10);
		const result = exponentialNumber.toFixed(exponent);
		return result;
	}
	return exponentialNumber;
};

export const formatValue = (responseData, dataObj) => {
	for (let i = 0; i < dataObj.length; i++) {
		const { scaling, currency, decimalPlaces, negativeNoFormat, region } = dataObj[i];
		responseData[i] = getFormattedValue(
			responseData[i],
			scaling,
			currency,
			decimalPlaces,
			negativeNoFormat,
			region
		);
	}
	return responseData;
};

export const getFormattedValue = (value, scale, currency, decimalPlaces, negativeNoFormat, region) => {
	let formattedValue = value;
	if (isNaN(value) || typeof value !== "number") {
		return formattedValue;
	}
	if (
		scale == undefined &&
		currency == undefined &&
		decimalPlaces == undefined &&
		negativeNoFormat == undefined &&
		region == undefined
	) {
		return formattedValue;
	}
	const isNegative = value < 0;
	let suffix;
	let denominator;
	if (scaleMapping[scale]) {
		suffix = scaleMapping[scale].suffix;
		denominator = scaleMapping[scale].denominator;
		formattedValue = convertExponentialToDecimal(value / denominator);
	}

	if (NumberFormatMap[region]) {
		const locale = NumberFormatMap[region].code;
		formattedValue = new Intl.NumberFormat(locale, { maximumFractionDigits: decimalPlaces }).format(
			formattedValue
		);
	} else if (decimalPlaces) {
		formattedValue = +formattedValue.toFixed(decimalPlaces);
	}

	if (isNegative && negativeNoFormat == "None") {
		formattedValue.slice(1);
	}

	currency = (currency && currency.split("-")[1]) || "";
	suffix = suffix || "";
	if (currency || suffix) {
		formattedValue = `${currency} ${formattedValue} ${suffix}`;
		return formattedValue.trim();
	}
	return formattedValue;
};

export const getSecureImage = img => {
	if (img && img.toString().includes("secureContainer")) {
		return Array.isArray(img) && img.length === 1
			? `${img.toString()}?access_token=${localStorage.$LoopBack$tempAccessTokenId ||
					localStorage.$LoopBack$accessTokenId}`
			: `${img}?access_token=${localStorage.$LoopBack$tempAccessTokenId ||
					localStorage.$LoopBack$accessTokenId}`;
	}
	return img;
};

export const sankeyChartTranformationFunction = (responseData, dataObj) => {
	const result = {
		nodes: [],
		links: []
	};
	const { seriesOn } = dataObj[0];
	const response = responseData[0];
	const nodesArray = [];
	const links = [];
	const [node1, node2] = seriesOn;

	if (seriesOn.length === 2) {
		// eslint-disable-next-line guard-for-in
		for (const i in response) {
			const record = response[i];
			if (record[node1] && record[node2]) {
				if (nodesArray.indexOf(node1) === -1) nodesArray.push(record[node1]);
				if (nodesArray.indexOf(node2) === -1) nodesArray.push(record[node2]);
				links.push({
					from: record[node1],
					to: record[node2],
					value: record.value || 0
				});
			}
		}

		result.nodes = nodesArray.map(ele => {
			return { label: ele };
		});
		result.links = links;
	}

	return result;
};

export const simplePyramidTranformationFunction = (responseData, dataObj) => {
    const result = [];
    const { seriesOn } = dataObj[0];
    const dimension = seriesOn[0];
    let response = responseData[0];
    response = response.sort((a, b) => a.value - b.value);

    for (const i in response) {
        const record = response[i];
        if (record[dimension]) {
            const rawLabel = record[dimension];
            const normalizedLabel = normalizeLabelForFusionCharts(rawLabel);

            result.push({
                label: rawLabel,
                value: record.value,
                link: dataObj[0].drilldownSeries || dataObj[0].drilldownSeriesList
                    ? `newchart-json-${normalizedLabel}`
                    : null
            });
        }
    }

    return result;
};

export const simplePyramidLinkedDataTransformation = (responseData, { data: dataObj, props }) => {
	const parentKey = dataObj[0].seriesOn[0];
	const baseDrilldownList =
		dataObj[0].drilldownSeriesList && Array.isArray(dataObj[0].drilldownSeriesList)
			? dataObj[0].drilldownSeriesList
			: dataObj[0].drilldownSeries
			? [dataObj[0].drilldownSeries]
			: [];

	// Backward compatibility: no drilldown or single-level behaves as before
	if (baseDrilldownList.length <= 1) {
		const result = {};
		const labelKey = dataObj[0].drilldownSeries;
		for (const item of responseData[0]) {
			const parentLabel = transformDateTypeData(item[parentKey]);
			const label = transformDateTypeData(item[labelKey]);
			if (result.hasOwnProperty(parentLabel)) {
				result[parentLabel].linkedchart.data.push({
					label,
					value: item.value
				});
			} else {
				// Use normalized parentLabel for id to match link format from base chart
				const normalizedParentId = normalizeLabelForFusionCharts(parentLabel);
				result[parentLabel] = {
					id: normalizedParentId,
					linkedchart: {
						chart: { ...props, theme: "fusion" },
						data: [
							{
								label,
								value: item.value
							}
						]
					}
				};
			}
		}
		return Object.values(result);
	}

	// Multi-level drilldown (all configured levels)
	const drilldownList = baseDrilldownList;

	// Build an in-memory tree of dimension values
	const tree = {};
	for (const item of responseData[0]) {
		const parentLabel = transformDateTypeData(item[parentKey]);
		if (!tree[parentLabel]) {
			tree[parentLabel] = { children: {}, value: 0 };
		}
		let currentNode = tree[parentLabel];
		for (let level = 0; level < drilldownList.length; level += 1) {
			const dimKey = drilldownList[level];
			const dimLabel = transformDateTypeData(item[dimKey]);
			if (!currentNode.children[dimLabel]) {
				currentNode.children[dimLabel] = { children: {}, value: 0 };
			}
			currentNode = currentNode.children[dimLabel];
		}
		currentNode.value += item.value;
	}

	// Helper function to calculate aggregated value for a node (sum of all leaf descendants)
	const calculateNodeValue = node => {
		if (Object.keys(node.children).length === 0) {
			return node.value;
		}
		let sum = 0;
		Object.keys(node.children).forEach(childLabel => {
			sum += calculateNodeValue(node.children[childLabel]);
		});
		return sum;
	};

	// Use the shared normalization function (MUST match normalization in singleSeriesTranformationFunction)
	const normalizeLabel = normalizeLabelForFusionCharts;

	// Helper to generate id from path - MUST use normalized labels to match link format
	// FusionCharts strips "newchart-json-" prefix and matches remaining part to ID
	const makeIdFromPath = path => path.map(normalizeLabel).join("__");
	const makeLinkFromPath = path => `newchart-json-${path.map(normalizeLabel).join("__")}`;

	const baseChartProps = { ...props, theme: "fusion" };
	const linkedData = [];

	// First level: parent dimension -> first drilldown dimension
	Object.keys(tree).forEach(parentLabel => {
		const node = tree[parentLabel];
		const data = Object.keys(node.children).map(childLabel => {
			const childNode = node.children[childLabel];
			const hasDeeperLevels = Object.keys(childNode.children).length > 0 && drilldownList.length > 1;
			const path = [parentLabel, childLabel];
			// Calculate aggregated value for this child node
			const aggregatedValue = calculateNodeValue(childNode);
			return {
				label: childLabel,
				value: aggregatedValue,
				...(hasDeeperLevels ? { link: makeLinkFromPath(path) } : {})
			};
		});

		// Use normalized parentLabel for id to match link format from base chart
		linkedData.push({
			id: makeIdFromPath([parentLabel]),
			linkedchart: {
				chart: baseChartProps,
				data
			}
		});

		// Recursively build all deeper levels
		// path: array of labels from root to current level
		// currentNode: the node at the current level in the tree
		// levelIndex: which drilldown dimension we're currently showing (0 = first drilldown, 1 = second, etc.)
		const buildDeeperLevels = (currentNode, path, levelIndex) => {
			if (levelIndex >= drilldownList.length) return;

			const children = Object.keys(currentNode.children);
			if (children.length === 0) return;

			const dataPoints = children.map(childLabel => {
				const childNode = currentNode.children[childLabel];
				const childPath = [...path, childLabel];
				const aggregatedValue = calculateNodeValue(childNode);
				
				const hasDeeperLevels = 
					Object.keys(childNode.children).length > 0 && 
					levelIndex + 1 < drilldownList.length;

				return {
					label: childLabel,
					value: aggregatedValue,
					...(hasDeeperLevels ? { link: makeLinkFromPath(childPath) } : {})
				};
			});

			const chartId = makeIdFromPath(path);
			linkedData.push({
				id: chartId,
				linkedchart: {
					chart: baseChartProps,
					data: dataPoints
				}
			});

			children.forEach(childLabel => {
				const childNode = currentNode.children[childLabel];
				const childPath = [...path, childLabel];
				if (Object.keys(childNode.children).length > 0 && levelIndex + 1 < drilldownList.length) {
					buildDeeperLevels(childNode, childPath, levelIndex + 1);
				}
			});
		};

		// Start building deeper levels from the first drilldown level
		Object.keys(node.children).forEach(firstLevelLabel => {
			const firstLevelNode = node.children[firstLevelLabel];
			if (Object.keys(firstLevelNode.children).length > 0 && drilldownList.length > 1) {
				buildDeeperLevels(firstLevelNode, [parentLabel, firstLevelLabel], 1);
			}
		});
	});

	return linkedData;
};

// Transformation function for nested/multi-level pie chart
export const nestedPieTranformationFunction = (responseData, dataObj) => {
	const { seriesOn } = dataObj[0];
	const response = responseData[0];
	
	if (!response || response.length === 0) {
		return { category: [] };
	}
	
	// Default color palette for nested pie
	const defaultColors = [
		"#f8bd19", "#e44a00", "#008ee4", "#33bdda", 
		"#6baa01", "#583e78", "#f45b00", "#8e0000"
	];
	
	// Build nested structure based on dimensions
	const buildNestedCategory = (data, dimensions, depth = 0) => {
		if (dimensions.length === 0) {
			return [];
		}
		
		const currentDimension = dimensions[0];
		const remainingDimensions = dimensions.slice(1);
		
		// Group data by current dimension
		const groups = {};
		for (const item of data) {
			const key = transformDateTypeData(item[currentDimension]) || "Unknown";
			if (!groups[key]) {
				groups[key] = {
					items: [],
					totalValue: 0
				};
			}
			groups[key].items.push(item);
			groups[key].totalValue += parseFloat(item.value) || 0;
		}
		
		// Build category array
		const categories = [];
		let colorIndex = 0;
		
		for (const [label, group] of Object.entries(groups)) {
			const category = {
				label,
				value: group.totalValue.toString(),
				color: defaultColors[colorIndex % defaultColors.length]
			};
			
			// Recursively build child categories if more dimensions exist
			if (remainingDimensions.length > 0) {
				const childCategories = buildNestedCategory(
					group.items,
					remainingDimensions,
					depth + 1
				);
				if (childCategories.length > 0) {
					category.category = childCategories;
				}
			}
			
			categories.push(category);
			colorIndex++;
		}
		
		return categories;
	};
	
	// If only one dimension, build flat category list
	if (seriesOn.length === 1) {
		return {
			category: buildNestedCategory(response, seriesOn)
		};
	}
	
	// For multiple dimensions, wrap in a root category
	const rootCategories = buildNestedCategory(response, seriesOn);
	const totalValue = rootCategories.reduce(
		(sum, cat) => sum + parseFloat(cat.value), 
		0
	);
	
	return {
		category: [{
			label: dataObj[0].label || "Total",
			color: "#ffffff",
			value: totalValue.toString(),
			category: rootCategories
		}]
	};
};